import axios from 'axios'

const API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

/**
 * Sends a chat message with options to the backend.
 * @param {string} token - The authentication token.
 * @param {object} options - The message options.
 * @param {string} options.content - The text content of the message.
 * @param {FileList} [options.files] - The list of files to upload.
 * @param {string|null} [options.conversationId] - The ID of the current conversation.
 * @param {string|null} [options.arcanaId] - The ID of the Arcano to use for this conversation.
 * @param {string|null} [options.projectId] - The ID of the project to use for this conversation.
 * @param {boolean} options.enableWebSearch - Flag to enable web search.
 * @param {boolean} options.enableDeepAnalysis - Flag to enable deep analysis.
 * @param {boolean} options.enableKnowledgeBase - Flag to enable knowledge base retrieval.
 * @returns {Promise<object>} - A promise that resolves to the API response data.
 */
export const sendMessage = async (token, options) => {
	const {
		content,
		files,
		conversationId,
		arcanaId,
		projectId,
		enableWebSearch,
		enableDeepAnalysis,
		enableKnowledgeBase,
	} = options

	const formData = new FormData()
	formData.append('content', content)

	if (files) {
		for (let i = 0; i < files.length; i++) {
			formData.append('files', files[i])
		}
	}

	if (conversationId) {
		formData.append('conversation_id', conversationId)
	}

	// FIXED: Add arcana_id to FormData (not URL) only when starting a new conversation
	if (arcanaId && !conversationId) {
		formData.append('arcana_id', arcanaId)
	}

	// Add project_id to FormData when provided
	if (projectId) {
		formData.append('project_id', projectId)
	}

	// Append all options to the form data, converting booleans to strings for reliability.
	formData.append('enable_web_search', String(enableWebSearch))
	formData.append('enable_deep_analysis', String(enableDeepAnalysis))
	formData.append('enable_knowledge_base', String(enableKnowledgeBase))

	// Use clean URL without query parameters
	const url = `${API_BASE_URL}/chat/message`

	try {
		const response = await axios.post(url, formData, {
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data
	} catch (error) {
		// The calling UI component can catch this error to implement a "Resend" button
		// by simply calling this `sendMessage` function again with the same `options`.
		console.error('Error sending message:', error)
		throw error
	}
}

/**
 * Requests the backend to regenerate the last AI response in a conversation.
 * This is used for a "reload" or "regenerate" button when a response is unsatisfactory or fails to generate correctly.
 * @param {string} token - The authentication token.
 * @param {object} options - The regeneration options.
 * @param {string} options.conversationId - The ID of the current conversation.
 * @param {string|null} [options.arcanaId] - The ID of the Arcano to use for regeneration.
 * @param {string|null} [options.projectId] - The ID of the project to use for regeneration.
 * @param {boolean} options.enableWebSearch - Flag to enable web search for the regeneration.
 * @param {boolean} options.enableDeepAnalysis - Flag to enable deep analysis for the regeneration.
 * @param {boolean} options.enableKnowledgeBase - Flag to enable knowledge base retrieval for the regeneration.
 * @returns {Promise<object>} - A promise that resolves to the API response data (the new AI message).
 */
export const regenerateResponse = async (token, options) => {
	const {
		conversationId,
		arcanaId,
		projectId,
		enableWebSearch,
		enableDeepAnalysis,
		enableKnowledgeBase,
	} = options

	if (!conversationId) {
		throw new Error('A conversationId is required to regenerate a response.')
	}

	const body = {
		enable_web_search: enableWebSearch,
		enable_deep_analysis: enableDeepAnalysis,
		enable_knowledge_base: enableKnowledgeBase,
	}

	// Build the URL with arcana_id and project_id parameters if provided
	let url = `${API_BASE_URL}/chat/conversations/${conversationId}/regenerate`
	const params = new URLSearchParams()
	if (arcanaId) {
		params.append('arcana_id', arcanaId)
	}
	if (projectId) {
		params.append('project_id', projectId)
	}
	if (params.toString()) {
		url += `?${params.toString()}`
	}

	try {
		const response = await axios.post(url, body, {
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			},
		})
		return response.data
	} catch (error) {
		console.error('Error regenerating response:', error)
		throw error
	}
}

/**
 * Fetches conversations for the current user.
 * @param {string} token - The authentication token.
 * @param {string} [searchQuery] - Optional search term.
 * @param {string} [arcanaId] - Optional arcana ID to filter conversations. Use 'general' for non-arcana conversations.
 * @param {string} [projectId] - Optional project ID to filter conversations. Use empty string for non-project conversations.
 * @returns {Promise<Array>} - A promise that resolves to the list of conversations.
 */
export const getConversations = async (
	token,
	searchQuery,
	arcanaId,
	projectId
) => {
	const url = new URL(`${API_BASE_URL}/chat/conversations`)
	if (searchQuery && searchQuery.trim()) {
		url.searchParams.append('search', searchQuery.trim())
	}

	// --- THIS LOGIC IS NOW CRITICAL ---
	if (projectId) {
		// When a project is selected, only show conversations from that project
		url.searchParams.append('project_id', projectId)
	} else if (arcanaId) {
		// When an arcana is selected (but no project), show conversations with that arcana and no project
		url.searchParams.append('arcana_id', arcanaId)
		url.searchParams.append('project_id', '') // Explicitly request conversations with NO project
	} else {
		// When neither is selected, show general conversations (no arcana, no project)
		url.searchParams.append('arcana_id', 'general')
		url.searchParams.append('project_id', '') // Explicitly request conversations with NO project
	}

	try {
		const response = await axios.get(url.toString(), {
			headers: {Authorization: `Bearer ${token}`},
		})
		return response.data
	} catch (error) {
		console.error('❌ Error fetching conversations:', error)

		// If the backend doesn't support filtering yet, fall back to getting all conversations
		if (error.response?.status === 400 && (arcanaId || projectId)) {
			const fallbackUrl = new URL(`${API_BASE_URL}/chat/conversations`)
			if (searchQuery && searchQuery.trim()) {
				fallbackUrl.searchParams.append('search', searchQuery.trim())
			}
			const fallbackResponse = await axios.get(fallbackUrl.toString(), {
				headers: {Authorization: `Bearer ${token}`},
			})

			// Apply client-side filtering as fallback
			const allConversations = fallbackResponse.data

			const filteredConversations = allConversations.filter((conv) => {
				// Project filtering takes priority
				if (projectId) {
					const convProjectId = String(conv.project_id || '')
					const targetProjectId = String(projectId)
					const isMatch = convProjectId === targetProjectId

					return isMatch
				}

				// Arcano filtering - STRICTLY EXCLUDE project conversations
				if (arcanaId === 'general') {
					// For general chat, show conversations with no arcana assigned and ABSOLUTELY NO PROJECT
					const hasNoArcano =
						!conv.active_arcana_id ||
						conv.active_arcana_id === null ||
						conv.active_arcana_id === undefined ||
						conv.active_arcana_id === ''
					const hasNoProject =
						!conv.project_id ||
						conv.project_id === null ||
						conv.project_id === undefined ||
						conv.project_id === ''
					const isGeneral = hasNoArcano && hasNoProject
					console.warn(
						`🔍 Conversation "${conv.title}" (arcana_id: "${conv.active_arcana_id}", project_id: "${conv.project_id}") - isGeneral: ${isGeneral} (noArcano: ${hasNoArcano}, noProject: ${hasNoProject})`
					)
					if (!isGeneral) {
						console.warn(
							`❌ EXCLUDING project conversation from general chat: "${conv.title}"`
						)
					}
					return isGeneral
				} else {
					// For specific arcana, show only conversations with that arcana and ABSOLUTELY NO PROJECT
					const convArcanaId = String(conv.active_arcana_id || '')
					const targetArcanaId = String(arcanaId)
					const hasCorrectArcano = convArcanaId === targetArcanaId
					const hasNoProject =
						!conv.project_id ||
						conv.project_id === null ||
						conv.project_id === undefined ||
						conv.project_id === ''
					const isMatch = hasCorrectArcano && hasNoProject

					return isMatch
				}
			})

			return filteredConversations
		}

		throw error
	}
}

/**
 * Fetches the details of a specific conversation.
 * @param {string} token - The authentication token.
 * @param {string} conversationId - The ID of the conversation to fetch.
 * @returns {Promise<object>} - A promise that resolves to the conversation details.
 */
export const getConversationDetails = async (token, conversationId) => {
	const response = await axios.get(
		`${API_BASE_URL}/chat/conversations/${conversationId}`,
		{
			headers: {Authorization: `Bearer ${token}`},
		}
	)
	return response.data
}

/**
 * Updates the title of a specific conversation.
 * @param {string} token - The authentication token.
 * @param {string} conversationId - The ID of the conversation to update.
 * @param {string} newTitle - The new title for the conversation.
 * @returns {Promise<object>} - A promise that resolves to the updated conversation summary.
 */
export const updateConversationTitle = async (
	token,
	conversationId,
	newTitle
) => {
	const response = await axios.patch(
		`${API_BASE_URL}/chat/conversations/${conversationId}/title`,
		{title: newTitle},
		{
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			},
		}
	)
	return response.data
}

/**
 * Persists metadata (e.g. visual explanations) on a specific message in a conversation.
 * @param {string} token - The authentication token.
 * @param {string} conversationId - The ID of the conversation.
 * @param {string} messageId - The ID of the message to update.
 * @param {object} metadata - The metadata object to merge into the message.
 * @returns {Promise<object>}
 */
export const updateMessageMetadata = async (
	token,
	conversationId,
	messageId,
	metadata
) => {
	const response = await axios.patch(
		`${API_BASE_URL}/chat/conversations/${conversationId}/message-metadata`,
		{message_id: messageId, metadata},
		{
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			},
		}
	)
	return response.data
}

/**
 * Deletes a specific conversation.
 * @param {string} token - The authentication token.
 * @param {string} conversationId - The ID of the conversation to delete.
 */
export const deleteConversation = async (token, conversationId) => {
	await axios.delete(`${API_BASE_URL}/chat/conversations/${conversationId}`, {
		headers: {Authorization: `Bearer ${token}`},
	})
}

/**
 * Deletes all conversations for the current user.
 * @param {string} token - The authentication token.
 */
export const deleteAllConversations = async (token) => {
	await axios.delete(`${API_BASE_URL}/chat/conversations`, {
		headers: {Authorization: `Bearer ${token}`},
	})
}

/**
 * Generates an enhanced "magic" prompt from a simple user idea for chat context.
 * @param {string} token - The user's authentication token.
 * @param {string} simplePrompt - The user's basic prompt text.
 * @param {string} length - The desired length of the enhanced prompt ("short" | "medium" | "long"). Defaults to "medium".
 * @returns {Promise<string>} - The enhanced prompt text.
 */
export const getMagicPromptChat = async (token, simplePrompt, length = 'medium') => {
	const response = await axios.post(
		`${API_BASE_URL}/chat/magic-prompt`,
		{simple_prompt: simplePrompt, length: length},
		{
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			},
		}
	)
	return response.data.magic_prompt
}
